// 원격 세션(탭) 공통 타입 — 메인 창·세션 창·세션 화면이 함께 쓴다(종전엔 App·SessionView 에 중복 정의).
import type { MonitorInfo, RtcClient, RtcStats } from '../rtc';
import type { ClipCaps } from '../clipboard/protocol';
import type { FileCaps } from '../files/protocol';
import type { QualityApplied, QualityCaps } from '../quality/protocol';
import type { ChatCaps } from '../chat/protocol';
import type { DrawCaps } from '../draw/channel';

/** 영상이 도착한 원격 세션의 화면 상태(연결된 RtcClient + 수신 스트림 + 통계). */
export interface Session {
  client: RtcClient;
  stream: MediaStream;
  stats: RtcStats | null;
  state: string;
  ok: boolean | null;
  /** 계정 없는 즉석 접속(§17.9.1 Phase E) 세션 여부. */
  anon?: boolean;
  /** 익명 세션의 강제 종료 예정 시각(epoch ms) — 카운트다운 표시용. */
  deadline?: number;
  /** 다중 모니터: 원격 모니터 목록(2개 이상이면 전환 UI 노출). */
  monitors?: MonitorInfo[];
  /** 현재 캡처 중인 모니터 인덱스. */
  currentMonitor?: number;
  /**
   * 상대 기기가 원격 입력 제어를 허용하고 있는지(A4 실시간 스위치).
   * `undefined` = 아직 모름(입력 채널이 열리기 전). 상대가 끄면 `false` — 이유를 화면에 띄워야 고장으로 오해받지 않는다.
   */
  controlAllowed?: boolean;
  /**
   * 클립보드 공유(§9.3.1) 능력 — 에이전트가 clip 채널로 알린 지원 종류·상한.
   * `undefined` = 아직 모름 또는 **구버전 에이전트**(채널 없음) → 버튼을 숨기고 Ctrl+V/C 를 평소처럼 원격에 보낸다.
   */
  clipCaps?: ClipCaps;
  /**
   * 파일 전송(§9.4) 능력 — 에이전트 file 채널 hello. `undefined` = 모름 또는 구버전 에이전트(파일 기능 숨김).
   */
  fileCaps?: FileCaps;
  /**
   * 화면 품질·FPS 조절(§9.5) 능력 — 에이전트 ctrl 채널 `qcap`.
   * `undefined` = 모름 또는 **구버전 에이전트**(채널 없음) → 품질 UI 를 숨긴다.
   * 🔴 다른 능력과 달리 **보기 전용 세션에도 온다**(화질은 제어가 아니라 보는 쪽 편의).
   */
  qualityCaps?: QualityCaps;
  /** 에이전트가 **실제로 적용한** 화질(요청값이 아니다 — 상한·업스케일 금지로 달라질 수 있다). */
  qualityApplied?: QualityApplied;
  /** 마지막 화질 변경 실패 문구(성공하면 지운다). */
  qualityError?: string;
  /**
   * 채팅(§9.6) 능력 — 에이전트 chat 채널 `hello`.
   * `undefined` = 모름 또는 **구버전 에이전트·`RS_AGENT_CHAT=0`**(채널 없음) → 채팅 단추를 숨긴다.
   * 🔴 화질과 같이 **보기 전용 세션에도 온다**. `ui === 'none'` 이면 상대에게 표시할 화면이 없다
   * (입력을 잠그고 사유를 보여야 한다 — 보내 놓고 아무도 못 보는 상황을 만들지 않는다).
   */
  chatCaps?: ChatCaps;
  /** 화면 그리기(§9.8) 능력 — `undefined` = 채널 없음(구버전 에이전트) → 뷰어 단독으로만 그린다. */
  drawCaps?: DrawCaps;
  /**
   * 이 세션에 허용된 기능(§14.19.2 — 서버 승인 통지). `undefined` = 모름(옛 서버) → 막지 않는다.
   * 판정은 `featureAllowed(session.features, …)` 한 곳으로 한다. 새 에이전트는 허용되지 않은 기능의 채널을 아예 만들지 않아
   * 능력(caps)이 오지 않으므로 이중으로 숨는다 — 이 값은 **구버전 에이전트** 기간의 화면 숨김을 맡는다.
   */
  features?: string[];
}

/**
 * 세션 하나를 여는 요청 — 메인 창이 만들어 세션 창으로 넘긴다(창 간 전달이라 **직렬화 가능한 값만**).
 *
 * ⚠️ `token`(로그인 액세스 토큰)이 들어 있다 — 같은 오리진 창 사이(postMessage)·앱 내부(IPC)로만 넘기고
 *    **로그에 남기지 않는다**(Tauri 쪽 대기열도 내용을 찍지 않는다).
 */
/**
 * 로그인 계정 접속의 "다음부터 묻지 않고 접속"(trustedDevices.ts · 기기의 허용 계정).
 * - `remember`: 이번 비밀번호로 승인되면 기기가 이 계정을 비밀번호 없이 허용하도록 요청한다.
 * - `trusted`: 기기가 이미 허용했다고 기억해 **비밀번호 없이** 보내는 접속 — 거부되면 기억을 지우고 다시 묻는다.
 */
export interface ConnectOptions {
  remember?: boolean;
  trusted?: boolean;
}

export type SessionRequest =
  // orgContext = 수퍼유저가 고른 조직(§14.19.1). 세션 창은 이 값을 REST 머리로 실어 보낸다(없으면 일반 사용자).
  | ({ kind: 'user'; target: string; password?: string; token: string; label?: string; orgContext?: string } & ConnectOptions)
  | { kind: 'anon'; code: string; password: string; label?: string };

/** 같은 대상 판정 키 — 이미 열린 대상을 또 열면 새 탭 대신 기존 탭으로 간다(에이전트는 동시 1세션이라 앞 탭이 죽는다). */
export function requestKey(req: SessionRequest): string {
  return req.kind === 'user' ? `user:${req.target}` : `anon:${req.code}`;
}

/** 탭 제목(사용자가 준 이름이 없으면 기기 코드/대상). */
export function requestLabel(req: SessionRequest): string {
  return req.label || (req.kind === 'user' ? req.target : req.code);
}

/** 탭을 끌어 놓은 지점(포인터 좌표 — 화면 기준과 창 기준 둘 다, 논리 px). */
export interface DropPoint {
  screenX: number;
  screenY: number;
  clientX: number;
  clientY: number;
}

/**
 * 탭을 끌어 놓은 곳의 판정(TeamViewer·Chrome 식 창 간 탭 이동).
 * - `none`   : 자기 창 안이거나 분리할 수 없음(탭 1개) — 창 간 이동은 없다(자기 탭 줄 안이면 SessionTabs 가 순서 바꾸기).
 * - `dock`   : 다른 세션 창 위 — 그 창의 탭으로 합친다(`window` = 웹은 창 이름, 앱은 창 label).
 * - `detach` : 모든 세션 창 밖 — 그 자리에 새 세션 창을 만든다(화면 좌표·내용 크기, 논리 px).
 *
 * 🔴 앱(Tauri)은 이 모양 그대로 Rust `DropTarget`(serde tag `kind`)과 주고받는다 — 필드 이름을 바꾸면 양쪽을 같이 바꿀 것.
 */
export type DropTarget =
  | { kind: 'none' }
  | { kind: 'dock'; window: string }
  | { kind: 'detach'; x: number; y: number; width: number; height: number };

/** 탭의 단계: 접속 중 → 연결됨 → (실패 | 종료). */
export type TabPhase = 'connecting' | 'live' | 'failed' | 'ended';

/**
 * 진행 중인 탭인가(접속 중·연결됨) — 실패·종료 탭은 세션이 없다.
 * 같은 대상 재접속 판정(기존 탭으로 이동)과 업데이트 배너의 "원격 세션 중" 판정이 함께 쓴다.
 */
export function isTabLive(t: Pick<SessionTab, 'phase'>): boolean {
  return t.phase === 'connecting' || t.phase === 'live';
}

export interface SessionTab {
  id: string;
  key: string;
  label: string;
  request: SessionRequest;
  phase: TabPhase;
  /** 진행 상태 문구(접속 중) 또는 실패·종료 사유. */
  message?: string;
  /** 영상이 온 뒤의 세션 상태(`live` 에서만). */
  session?: Session;
}
